import { StarjackTable } from '@starjack/shared-components';
import { FunctionComponent, useMemo, useState } from 'react';
import { Voucher } from '../model/voucher.types';

import {
    useVoucherColumns,
    VoucherTableColumnId,
} from '../lib/hooks/useVoucherColumns';
import { Box, Drawer, SvgIcon, Typography } from '@mui/material';
import {
    useVoucherDetailsBlocksFactory,
    VoucherContentBlockId,
} from '../lib/hooks/useVoucherDetails';
import { VoucherDrawerBlock } from '../ui/voucherDrawerContent/voucherDrawerBlock';
import { FlexContainer } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@heroicons/react/24/solid/XMarkIcon';

export type VouchersOverviewContainerProps = {
    currentPageVouchers: Voucher[];
    vouchersTotal: number;
    vouchersPerPage: number;
    vouchersLoading: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    hideColumns?: VoucherTableColumnId[];
    hideDetailFields?: VoucherContentBlockId[];
    onRowClick?: (voucher: Voucher) => void;
    defaultSortColumn?: string;
    onDownload?: (voucher: Voucher) => Promise<void>;
};

export const VouchersOverviewContainer: FunctionComponent<
    VouchersOverviewContainerProps
> = ({
    currentPageVouchers = [],
    vouchersTotal,
    vouchersPerPage,
    vouchersLoading,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    defaultSortColumn,
    hideColumns = [],
    hideDetailFields = [],
    onDownload,
}) => {
    const [drawerVoucherId, setDrawerVoucherId] = useState<string>(null);

    const columns = useVoucherColumns(onDownload);

    const columnsFilteredByConfig = useMemo(() => {
        return columns.filter(
            (column) =>
                !hideColumns.find(
                    (hideColumn) => hideColumn === column.columnId
                )
        );
    }, [columns, hideColumns]);

    const drawerVoucher =
        currentPageVouchers.find((cT) => cT.id === drawerVoucherId) || null;

    // Get the factory function from hooks
    const createVoucherDetailsBlocks = useVoucherDetailsBlocksFactory();

    const voucherDrawerBlocks = useMemo(() => {
        if (!drawerVoucherId || !drawerVoucher) return [];

        return createVoucherDetailsBlocks({
            voucher: drawerVoucher,
        }).filter((block) => !hideDetailFields.includes(block.blockId));
    }, [
        drawerVoucherId,
        drawerVoucher,
        hideDetailFields,
        createVoucherDetailsBlocks,
    ]);

    return (
        <>
            <StarjackTable<Voucher>
                itemId={'id'}
                items={currentPageVouchers}
                columns={columnsFilteredByConfig}
                count={vouchersTotal}
                rowsPerPage={vouchersPerPage}
                loading={vouchersLoading}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                onRowClick={(item) => {
                    setDrawerVoucherId(item.id);

                    onRowClick?.(item);
                }}
                rowsSelectable={false}
                defaultSortColumn={defaultSortColumn || 'purchasedOn'}
                selectRow={(voucher) => voucher.id === drawerVoucher?.id}
            />
            <Drawer
                anchor='right'
                open={!!drawerVoucherId}
                onClose={() => {
                    setDrawerVoucherId(null);
                }}
                variant='persistent'
            >
                <Box
                    sx={{
                        width: '580px',
                    }}
                >
                    <FlexContainer
                        sx={(theme) => ({
                            height: '72px',
                            position: 'fixed',
                            width: '580px',
                            alignItems: 'center',
                            backgroundColor: theme.palette.primary.dark,
                            color: '#fff',
                            p: 4,
                        })}
                    >
                        <Typography variant='h6'>
                            {drawerVoucher?.title || drawerVoucher?.code || '-'}
                        </Typography>

                        <IconButton
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={() => {
                                setDrawerVoucherId(null);
                            }}
                        >
                            <SvgIcon
                                sx={{
                                    color: '#fff',
                                    fontSize: '28px',
                                }}
                            >
                                <CloseIcon />
                            </SvgIcon>
                        </IconButton>
                    </FlexContainer>

                    <Box
                        sx={{
                            mt: '72px',
                        }}
                    >
                        {voucherDrawerBlocks.map((block) => (
                            <VoucherDrawerBlock
                                key={block.blockId}
                                header={block.header}
                            >
                                {block.content}
                            </VoucherDrawerBlock>
                        ))}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
